import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import { useOrganizationAiCreditBalance } from "@shared/queryHooks/useOrganizationAiCreditBalance";
import {
  useCheckoutAiCreditTopUp,
  usePreviewAiCreditSubscriptionChange,
  useCommitAiCreditSubscriptionChange,
  usePreviewAiCreditTopUp,
  usePurchaseAiCreditTopUp,
  usePurchaseAiCreditTopUpCheckoutSession,
  useCancelAiCreditSubscription,
  useRevertAiCreditSubscriptionCancellation,
  useAiCreditCustomerSubscription,
  useAiCreditSubscriptionSchedule,
  useCancelAiCreditScheduledChange,
} from "@shared/queryHooks/useOrganizationAiCreditPurchase";
import { useToastContext } from "@shared/context/ToastContext";
import { useModalContext } from "@shared/context/ModalContext";
import { useCurrentSession } from "@ats/src/context/CurrentSessionContext";

import { AI_CREDIT_PACK_DISPLAY_NAMES, AI_CREDIT_PACK_CREDITS_BY_LOOKUP_KEY } from "@shared/lib/planHelpers";
import { prettyDate } from "@shared/lib/time";

import CancelAiCreditSubscriptionConfirmModal from "@ats/src/views/accountAdmin/accountBilling/CancelAiCreditSubscriptionConfirmModal";
import PurchaseAiCreditTopUpConfirmModal from "./PurchaseAiCreditTopUpConfirmModal";
import UpdateAiCreditSubscriptionConfirmModal from "./UpdateAiCreditSubscriptionConfirmModal";
import AiSubscriptionTierCard, { AiCreditTier } from "./AiSubscriptionTierCard";
import AiCreditOneOffCard, { AiCreditOneOff } from "./AiCreditOneOffCard";
import AiSubscriptionStatus from "./AiSubscriptionStatus";
import LoadingIndicator from "@ats/src/components/shared/LoadingIndicator";
import SmallButton from "@ats/src/components/shared/SmallButton";
import { splitTiers, deriveTierButtonText, deriveTierButtonType, formatCents } from "./aiSubscriptionHelpers";

function AiCreditSubscription({ pricesData }: { pricesData: any }) {
  const { isLoading: isLoadingBalance } = useOrganizationAiCreditBalance();
  const {
    data: aiCreditCustomerSubscriptionData,
    isFetching: isFetchingAiCreditCustomerSubscription,
  } = useAiCreditCustomerSubscription({
    refetchOnWindowFocus: false,
  });
  const { mutate: subscribe, isLoading: isSubscribing } = useCheckoutAiCreditTopUp();
  const { mutate: previewSubscriptionChange, isLoading: isLoadingPreview } =
    usePreviewAiCreditSubscriptionChange();
  const { mutate: commitSubscriptionChange, isLoading: isCommittingChange } =
    useCommitAiCreditSubscriptionChange();
  const { mutate: previewTopUp, isLoading: isLoadingTopUpPreview } = usePreviewAiCreditTopUp();
  const { mutate: purchase, isLoading: isPurchasingDirect } = usePurchaseAiCreditTopUp();
  const { mutate: purchaseCheckoutSession, isLoading: isPurchasingCheckoutSession } =
    usePurchaseAiCreditTopUpCheckoutSession();
  const { mutate: cancelSubscription, isLoading: isCanceling } = useCancelAiCreditSubscription();
  const { mutate: revertCancellation, isLoading: isRevertingCancellation } = useRevertAiCreditSubscriptionCancellation();
  const { mutate: cancelScheduledChange, isLoading: isCancelingScheduledChange } = useCancelAiCreditScheduledChange();
  const addToast = useToastContext();
  const { openModal, removeModal } = useModalContext();
  const { currentOrganization } = useCurrentSession();

  const { subscriptionTiers, topUpTiers } = splitTiers(pricesData);
  const currentSubscription = aiCreditCustomerSubscriptionData
    ? aiCreditCustomerSubscriptionData.subscription
    : null;
  const currentPriceObject = currentSubscription && currentSubscription.items.data[0].price;
  const currentPlanLookupKey = currentPriceObject?.lookupKey;
  const isSubscribed =
    currentSubscription?.status === "active" || currentSubscription?.status === "past_due";
  // Resolve the active subscription's credits off the live Stripe lookupKey by
  // matching it against the same subscriptionTiers list the cards render from
  // (mirrors the analog deriving the current plan's rank by matching
  // currentPriceObject.lookupKey against the same plan options the cards use —
  // a single source, not a divergent second table).
  const currentSubscriptionTier =
    isSubscribed && currentPlanLookupKey
      ? subscriptionTiers.find((tier) => tier.lookupKey === currentPlanLookupKey)
      : null;
  const currentCredits = currentSubscriptionTier ? currentSubscriptionTier.credits : null;
  const currentPeriodEnd = currentSubscription?.currentPeriodEnd;
  const cancelAtPeriodEnd = currentSubscription?.cancelAtPeriodEnd;
  const cancelAt = currentSubscription?.cancelAt;
  const scheduleId = currentSubscription?.schedule || null;
  const { data: scheduleData } = useAiCreditSubscriptionSchedule(scheduleId);
  const hasScheduledChange = scheduleData?.schedule?.phases?.length > 1;
  const scheduledPlan = hasScheduledChange
    ? AI_CREDIT_PACK_DISPLAY_NAMES[scheduleData.schedule.phases[1].lookupKey] || scheduleData.schedule.phases[1].lookupKey
    : null;
  const currentPlanName = currentPlanLookupKey
    ? AI_CREDIT_PACK_DISPLAY_NAMES[currentPlanLookupKey] || currentPlanLookupKey
    : null;
  const isScheduledDowngrade = hasScheduledChange && scheduleData.schedule.phases[1].unitAmount < scheduleData.schedule.phases[0].unitAmount;

  const redirectToStripe = (data: { url: string }) => {
    window.location.href = data.url;
  };

  const handleSelectTier = (tier: AiCreditTier) => {
    const isDowngrade = currentCredits != null && tier.credits < currentCredits;

    previewSubscriptionChange(
      { priceId: tier.priceId },
      {
        onSuccess: (previewData) => {
          const newPlanName = AI_CREDIT_PACK_DISPLAY_NAMES[tier.lookupKey] || tier.name;
          const newPlanCredits = AI_CREDIT_PACK_CREDITS_BY_LOOKUP_KEY[tier.lookupKey] || tier.credits;

          const oldLine = previewData.lines.find((line) => line.amount < 0);
          const currentPlanLookupKeyFromPreview = oldLine ? oldLine.price.lookupKey : currentPlanLookupKey;
          const currentPlanNameFromPreview = currentPlanLookupKeyFromPreview
            ? AI_CREDIT_PACK_DISPLAY_NAMES[currentPlanLookupKeyFromPreview] || currentPlanLookupKeyFromPreview
            : "";

          const paymentMethodLabel = previewData.defaultPaymentMethod
            ? `${previewData.defaultPaymentMethod.brand.charAt(0).toUpperCase() + previewData.defaultPaymentMethod.brand.slice(1)} •••• ${previewData.defaultPaymentMethod.last4}`
            : "";

          openModal(
            <UpdateAiCreditSubscriptionConfirmModal
              onCancel={removeModal}
              onConfirm={() => {
                removeModal();
                commitSubscriptionChange(
                  { priceId: tier.priceId },
                  {
                    onSuccess: () => {
                      addToast({
                        title: isDowngrade
                          ? "Plan change scheduled"
                          : "Plan upgraded successfully",
                        kind: "success",
                      });
                    },
                    onError: (error: any) => {
                      const errorMessage =
                        error?.data?.errors?.general?.[0] ||
                        "Unable to change subscription. Please try again.";
                      addToast({ title: errorMessage, kind: "error", delay: 30000 });
                    },
                  },
                );
              }}
              isLoading={isCommittingChange}
              isDowngrade={isDowngrade}
              newPlanName={newPlanName}
              newPlanCredits={newPlanCredits}
              newMonthlyPrice={`$${tier.priceDollars.toFixed(2)}`}
              startDate={previewData.currentPeriodEnd ? prettyDate(previewData.currentPeriodEnd) : ""}
              paymentMethodLabel={paymentMethodLabel}
              amountDueToday={formatCents(previewData.amountDue)}
              currentPlanName={currentPlanNameFromPreview}
              creditForCurrentPlan={oldLine ? formatCents(oldLine.amount) : ""}
              currentPlanCredits={currentCredits}
            />,
          );
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to load subscription preview.";
          addToast({ title: errorMessage, kind: "error", delay: 30000 });
        },
      },
    );
  };

  /**
   * USED WHEN THE ORG HAS NO ACTIVE SUBSCRIPTION AND IS STARTING A NEW ONE.
   * Mirrors the analog's onCreateNewSubscription terminal (PlanCard.tsx:103):
   * the create-new path opens a Stripe Checkout session rather than the
   * change-subscription portal session (which requires an existing
   * subscriptionItemId). The checkout mutation posts the tier's lookupKey and
   * redirects to the Stripe-hosted checkout URL on success.
   */
  const handleCreateNewSubscription = (tier: AiCreditTier) => {
    subscribe(
      { stripePriceLookupKey: tier.lookupKey },
      {
        onSuccess: (data: { redirectUrl: string }) => {
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to start subscription.";
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  const handlePurchaseError = (error: any) => {
    if (error?.data?.errors?.["general"] != undefined) {
      window.logger(
        "%c[AiCreditSubscription] purchaseTopUp",
        "color: #FF0602",
        { error },
      );
      addToast({ title: error.data.errors.general[0], kind: "warning" });
    }
  };

  // Direct-charge path (card on file). Mirrors the WWR frontend's
  // handleCreateBoardWwrListing: call the direct-charge mutation. The user-facing
  // success growl is emitted server-side from grant_credits, so onSuccess shows no
  // toast (matching the analog). The mutation's onSuccess already invalidated the
  // credit balance query.
  const purchaseTopUp = (aiCreditTopUp: AiCreditOneOff) => {
    purchase(
      { stripePriceLookupKey: aiCreditTopUp.lookupKey },
      {
        onSuccess: () => {},
        onError: handlePurchaseError,
      },
    );
  };

  // Checkout-session path (no card on file). Calls the checkout-session
  // mutation, redirects to the Stripe-hosted checkout URL on success, and on
  // error shows an error toast with a fallback message. Loading state comes from
  // the mutation's isLoading (combined at the card), so no local flag is needed.
  const purchaseTopUpCheckoutSession = (aiCreditTopUp: AiCreditOneOff) => {
    purchaseCheckoutSession(
      { stripePriceLookupKey: aiCreditTopUp.lookupKey },
      {
        onSuccess: (data: { url: string; sessionId: string }) => {
          redirectToStripe({ url: data.url });
        },
        onError: (error: any) => {
          window.logger(
            "%c[AiCreditSubscription] purchaseTopUpCheckoutSession",
            "color: #FF0602",
            { error },
          );
          addToast({
            title: error?.data?.errors?.general?.[0] || "Failed to create checkout session",
            kind: "error",
          });
        },
      },
    );
  };

  const handleBuyAiCreditTopUp = (aiCreditTopUp: AiCreditOneOff) => {
    if (currentOrganization.stripeDefaultPaymentMethodOnFile) {
      previewTopUp(
        { stripePriceLookupKey: aiCreditTopUp.lookupKey },
        {
          onSuccess: (previewData: any) => {
            const paymentMethodLabel = previewData.defaultPaymentMethod
              ? `${previewData.defaultPaymentMethod.brand.charAt(0).toUpperCase() + previewData.defaultPaymentMethod.brand.slice(1)} •••• ${previewData.defaultPaymentMethod.last4}`
              : "Card on file";

            openModal(
              <PurchaseAiCreditTopUpConfirmModal
                credits={previewData.credits}
                amountDueToday={formatCents(previewData.amountDue)}
                paymentMethodLabel={paymentMethodLabel}
                onCancel={removeModal}
                onConfirm={() => {
                  removeModal();
                  purchaseTopUp(aiCreditTopUp);
                }}
              />,
            );
          },
          onError: (error: any) => {
            const errorMessage =
              error?.data?.errors?.general?.[0] || "Unable to load purchase preview.";
            addToast({ title: errorMessage, kind: "error", delay: 30000 });
          },
        },
      );
      return;
    }

    // No card on file -> Stripe Checkout redirect path (Stripe confirms there).
    purchaseTopUpCheckoutSession(aiCreditTopUp);
  };

  const handleRevertCancellation = () => {
    revertCancellation(null, {
      onSuccess: () => addToast({ title: "Cancellation reverted", kind: "success", delay: 30000 }),
      onError: (error: any) => {
        addToast({
          title: error?.data?.errors?.general?.[0] || "Failed to revert cancellation",
          kind: "error",
          delay: 30000,
        });
      },
    });
  };

  const handleCancelClick = () => {
    openModal(
      <CancelAiCreditSubscriptionConfirmModal
        onCancel={removeModal}
        onConfirm={() => {
          removeModal();
          cancelSubscription(null, {
            onSuccess: () => addToast({ title: "Subscription canceled", kind: "success" }),
            onError: (error: any) => {
              addToast({
                title: error?.data?.errors?.general?.[0] || "Failed to cancel subscription",
                kind: "warning",
                delay: 30000,
              });
            },
          });
        }}
        isLoading={isCanceling}
        planName={currentPlanLookupKey ? (AI_CREDIT_PACK_DISPLAY_NAMES[currentPlanLookupKey] || currentPlanLookupKey) : ""}
        periodEndsAt={currentPeriodEnd || null}
      />,
    );
  };

  if (isFetchingAiCreditCustomerSubscription) {
    return <LoadingIndicator label="Loading..." />;
  }

  return (
    <Styled.Section>
      <AiSubscriptionStatus
        isSubscribed={isSubscribed}
        currentCredits={currentCredits}
        planName={currentPlanLookupKey ? AI_CREDIT_PACK_DISPLAY_NAMES[currentPlanLookupKey] : null}
        periodEndsAt={currentPeriodEnd}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        cancelAt={cancelAt}
        isCanceling={isCanceling}
        isRevertingCancellation={isRevertingCancellation}
        onClickCancel={handleCancelClick}
        onClickRevertCancellation={handleRevertCancellation}
      />

      {hasScheduledChange && (
        <Styled.ScheduledCallout>
          <span>
            {isScheduledDowngrade
              ? `Your plan will be downgraded from ${currentPlanName} to ${scheduledPlan} on ${prettyDate(currentPeriodEnd)}.`
              : `Your plan will change to ${scheduledPlan} on ${prettyDate(currentPeriodEnd)}.`}
          </span>
          <SmallButton
            onClick={() => {
              cancelScheduledChange(
                { scheduleId: scheduleId },
                {
                  onSuccess: () => addToast({ title: "Scheduled change canceled", kind: "success", delay: 30000 }),
                  onError: (error: any) => {
                    addToast({
                      title: error?.data?.errors?.general?.[0] || "Failed to cancel scheduled change",
                      kind: "error",
                      delay: 30000,
                    });
                  },
                },
              );
            }}
            disabled={isCancelingScheduledChange}
            styleType="secondary"
          >
            Don't downgrade plan
          </SmallButton>
        </Styled.ScheduledCallout>
      )}

      <Styled.Subtitle>{isSubscribed ? "Change your plan" : "Choose a credit subscription"}</Styled.Subtitle>
      <Styled.OptionsContainer>
        <Styled.Options>
          {subscriptionTiers.map((tier) => {
            const isCurrent = currentSubscription?.plan?.id === tier.priceId;
            const buttonText = deriveTierButtonText(isSubscribed, currentCredits, tier.credits);
            return (
              <AiSubscriptionTierCard
                key={tier.lookupKey}
                tier={{
                  ...tier,
                  buttonText,
                  buttonType: deriveTierButtonType(buttonText),
                  isBestValue: tier.lookupKey.includes("_large"),
                }}
                isCurrentPlan={isCurrent}
                hasActiveSubscription={isSubscribed}
                cancelAtPeriodEnd={cancelAtPeriodEnd}
                isLoading={isSubscribing || isLoadingPreview || isCommittingChange || (hasScheduledChange && buttonText === "Downgrade")}
                isLoadingButton={isFetchingAiCreditCustomerSubscription}
                isCanceling={isCanceling}
                onSelect={handleSelectTier}
                onCreateNewSubscription={handleCreateNewSubscription}
                onClickCancel={handleCancelClick}
              />
            );
          })}
        </Styled.Options>
      </Styled.OptionsContainer>

      <Styled.Subtitle>One-time Plato credit top-ups</Styled.Subtitle>
      <Styled.Hint>Purchase additional credits anytime. No subscription required. Credits are available immediately and roll over each month.</Styled.Hint>
      <Styled.OptionsContainer>
        <Styled.Options>
          {topUpTiers.map((aiCreditTopUp) => (
            <AiCreditOneOffCard
              key={aiCreditTopUp.lookupKey}
              purchase={aiCreditTopUp}
              isLoading={isPurchasingDirect || isPurchasingCheckoutSession}
              onBuy={handleBuyAiCreditTopUp}
            />
          ))}
        </Styled.Options>
      </Styled.OptionsContainer>
    </Styled.Section>
  );
}

export default AiCreditSubscription;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Section = styled.section`
  label: AiCreditSubscription;
`;

Styled.ScheduledCallout = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditSubscription_ScheduledCallout;
    ${[t.rounded.md, t.py(3), t.px(4), t.mt(3)]}
    background-color: ${t.dark ? t.color.gray[800] : t.color.gray[100]};
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    ${[t.text.sm]}
    max-width: 696px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  `;
});

Styled.Subtitle = styled.h3((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditSubscription_Subtitle;
    ${[t.text.bold, t.mt(8)]}
    color: ${t.dark ? t.color.gray[200] : t.color.black};
    font-size: 1rem;
  `;
});

Styled.Hint = styled.p((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditSubscription_Hint;
    ${[t.text.sm, t.mt(1)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
  `;
});

Styled.OptionsContainer = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditSubscription_OptionsContainer;
    display: flex;
    flex-direction: column;
    ${[t.mt(4)]}
    max-width: 696px;
  `;
});

Styled.Options = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: AiCreditSubscription_Options;
    display: flex;
    flex-wrap: wrap;
    gap: ${t.spacing[3]};
  `;
});

